import React, { useMemo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client/react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.min.css';
import moment from 'moment';
import { Notify } from 'notiflix';

import { GET_EVENT_FEEDBACK_FORM } from 'helper/graphql/queries';
import { SUBMIT_EVENT_FEEDBACK } from 'helper/graphql/mutations';
import { PATH_EVENTS } from 'routes/Paths';
import { Link } from 'react-router-dom';

const EventFeedbackPage = () => {
	const { guid } = useParams();
	const [submitted, setSubmitted] = useState(false);

	const { data, loading, error } = useQuery(GET_EVENT_FEEDBACK_FORM, {
		variables: { guid },
		fetchPolicy: 'cache-and-network',
	});

	const event = useMemo(
		() => data?.allEvents?.edges?.[0]?.node || null,
		[data]
	);

	const [submitFeedback, { loading: submitting }] = useMutation(SUBMIT_EVENT_FEEDBACK);

	// Build SurveyJS model from schema
	const survey = useMemo(() => {
		if (!event?.feedbackFormSchema) return null;
		const raw = event.feedbackFormSchema;
		const schema = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const model = new Model(schema);
		model.showCompletedPage = false; // we handle the completion state ourselves
		return model;
	}, [event?.feedbackFormSchema]);

	useEffect(() => {
		if (!survey) return;
		survey.onComplete.add(async (sender) => {
			try {
				await submitFeedback({
					variables: {
						eventGuid: guid,
						feedbackData: sender.data,
					},
				});
				setSubmitted(true);
			} catch (err) {
				Notify.failure(err?.message || 'Failed to submit feedback. Please try again.');
				// Allow re-submission
				survey.clear(false, true);
			}
		});
	}, [survey]);

	// ── Loading ──────────────────────────────────────────────────────────────
	if (loading && !data) {
		return (
			<div className="efp-loading">
				<Spinner animation="border" size="sm" className="me-2" />
				Loading feedback form…
			</div>
		);
	}

	// ── Event not found ───────────────────────────────────────────────────────
	if (!loading && !event) {
		return (
			<Container className="py-5 text-center">
				<i className="fe fe-alert-circle efp-error-icon"></i>
				<h4 className="mt-3">Event not found</h4>
				<Link to={PATH_EVENTS.root} className="btn efp-btn mt-3">Back to Events</Link>
			</Container>
		);
	}

	// ── No feedback form configured ───────────────────────────────────────────
	if (!loading && event && !event.feedbackFormSchema) {
		return (
			<Container className="py-5 text-center">
				<i className="fe fe-file-text efp-info-icon"></i>
				<h4 className="mt-3">No feedback form available</h4>
				<p className="text-muted">The feedback form for this event has not been set up yet.</p>
				<Link to={PATH_EVENTS.root} className="btn efp-btn mt-3">Back to Events</Link>
			</Container>
		);
	}

	// ── Submitted success ─────────────────────────────────────────────────────
	if (submitted) {
		return (
			<div className="efp-page pt-lg-8 pb-lg-16 pt-8 pb-12">
				<Container>
					<Row className="justify-content-center">
						<Col xs={12} md={8} lg={6}>
							<div className="efp-success">
								<div className="efp-success-icon-wrap">
									<i className="fe fe-check efp-success-icon"></i>
								</div>
								<h3 className="efp-success-title">Thank you!</h3>
								<p className="efp-success-sub">
									Your feedback has been submitted. Your e-certificate of attendance will be sent to your email shortly.
								</p>
								<Link to={PATH_EVENTS.certificates} className="btn efp-btn">
									<i className="fe fe-award me-2"></i>View My Certificates
								</Link>
							</div>
						</Col>
					</Row>
				</Container>
			</div>
		);
	}

	// ── Feedback form ─────────────────────────────────────────────────────────
	return (
		<div className="efp-page">
			<Container>
				<Row className="justify-content-center">
					<Col xs={12} md={10} lg={8}>
						{/* Event header */}
						<div className="efp-event-header mb-4">
							<p className="efp-event-type mb-1">{event?.eventType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
							<h2 className="efp-event-title">{event?.title}</h2>
							<div className="efp-event-meta">
								{event?.sessionDatetime && (
									<span className="efp-meta-item">
										<i className="fe fe-calendar me-1"></i>
										{moment(event.sessionDatetime).format('MMMM D, YYYY')}
									</span>
								)}
								{event?.speakerName && (
									<span className="efp-meta-item">
										<i className="fe fe-mic me-1"></i>
										{event.speakerName}
									</span>
								)}
							</div>
						</div>

						{/* Survey */}
						<Card className="efp-survey-card">
							<Card.Body className="p-0">
								{survey && <Survey model={survey} />}
							</Card.Body>
						</Card>

						<p className="efp-footer-note mt-3">
							<i className="fe fe-award me-1"></i>
							Completing this form will trigger your e-certificate of attendance.
						</p>
					</Col>
				</Row>
			</Container>

			<style>{`
				.efp-loading {
					display: flex; align-items: center; justify-content: center;
					min-height: 50vh; color: #94a3b8; font-size: 0.875rem;
				}
				.efp-error-icon, .efp-info-icon {
					font-size: 3rem; color: #e2e8f0; display: block; margin-bottom: 0.5rem;
				}
				.efp-page { padding: 2.5rem 0 4rem; }
				.efp-event-header { text-align: center; }
				.efp-event-type {
					font-size: 0.75rem; font-weight: 700; color: #F68521;
					text-transform: uppercase; letter-spacing: 0.5px;
				}
				.efp-event-title {
					font-size: 1.75rem; font-weight: 800; color: #1e293b; margin-bottom: 0.75rem;
				}
				.efp-event-meta {
					display: flex; align-items: center; justify-content: center;
					gap: 1.25rem; flex-wrap: wrap;
				}
				.efp-meta-item { font-size: 0.875rem; color: #64748b; display: flex; align-items: center; }
				.efp-survey-card {
					border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden;
					box-shadow: 0 4px 16px rgba(0,0,0,0.06);
				}
				.efp-footer-note {
					font-size: 0.8125rem; color: #94a3b8; text-align: center;
					display: flex; align-items: center; justify-content: center;
				}
				.efp-success {
					text-align: center; padding: 3rem 2rem;
					border: 1px solid #e2e8f0; border-radius: 20px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.06);
					background: white;
				}
				.efp-success-icon-wrap {
					width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 1.5rem;
					background: linear-gradient(135deg, #10b981, #059669);
					display: flex; align-items: center; justify-content: center;
					box-shadow: 0 8px 20px rgba(16,185,129,0.3);
				}
				.efp-success-icon { font-size: 2.25rem; color: white; }
				.efp-success-title { font-size: 1.625rem; font-weight: 800; color: #1e293b; margin-bottom: 0.75rem; }
				.efp-success-sub { font-size: 0.9375rem; color: #64748b; max-width: 360px; margin: 0 auto 1.5rem; }
				.efp-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px;
					padding: 0.625rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
					display: inline-flex; align-items: center;
				}
				.efp-btn:hover { opacity: 0.9; color: white; }
			`}</style>
		</div>
	);
};

export default EventFeedbackPage;
